/**
 * "Leave a note" when someone leaves a shared project after changing it (collaboration phase 3). The Code view says
 * when this tab arrives in a project and when it leaves it (markArrived / markLeft); on leaving, when the project is
 * shared and the person (or their agents) changed something since they arrived, the offer is published to the
 * LeaveNotePrompt (mounted once, outside the project view, so it survives the navigation). Closing the browser tab
 * cannot show a custom dialog: only in-app navigation offers it.
 */
import { getAccess, getChanges } from '../services/CollabService';
import { changedSince } from './collab3Model';

const KEY = (projectId) => `collab-arrived:${projectId}`;
const listeners = new Set();
// Projects this tab is in right now: a remount of the view (leave + arrive within LEAVE_GRACE_MS) is not a leave.
const present = new Set();
const LEAVE_GRACE_MS = 1500;

const storage = () => {
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null; } catch { return null; }
};

export function markArrived(projectId, now = new Date()) {
  if (!projectId) { return; }
  present.add(projectId);
  // The first arrival of this visit counts (a remount keeps it).
  if (!storage()?.getItem(KEY(projectId))) { storage()?.setItem(KEY(projectId), now.toISOString()); }
}

/** When this tab arrived in the project (ISO), or null. */
export const arrivedAt = (projectId) => (projectId ? storage()?.getItem(KEY(projectId)) || null : null);

/** The offer for these answers: `{ count }` when the project is shared and you changed something, else null. */
export function offerFor(access, changes, since) {
  const shared = (access?.members || []).length > 0;
  if (!shared || !['owner', 'editor'].includes(access?.role)) { return null; }
  const count = changedSince(changes, since);
  return count > 0 ? { count } : null;
}

export async function markLeft(projectId, name = '', { access = getAccess, changes = getChanges, graceMs = LEAVE_GRACE_MS } = {}) {
  if (!projectId) { return null; }
  present.delete(projectId);
  if (graceMs > 0) { await new Promise((resolve) => { setTimeout(resolve, graceMs); }); }
  if (present.has(projectId)) { return null; }
  const since = storage()?.getItem(KEY(projectId)) || null;
  storage()?.removeItem(KEY(projectId));
  if (!since) { return null; }
  try {
    const [who, list] = await Promise.all([access(projectId), changes(projectId, { since, limit: 200 })]);
    const offer = offerFor(who, list, since);
    if (!offer) { return null; }
    const event = { projectId, name, since, count: offer.count };
    listeners.forEach((listener) => { try { listener(event); } catch { /* never breaks the others */ } });
    return event;
  } catch {
    return null;
  }
}

export function onLeaveOffer(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
